import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  Calendar,
  Check,
  ChevronDown,
  ChevronLeft,
  Clock,
  Code2,
  FolderKanban,
  Link2,
  ListFilter,
  Plus,
  Search,
  SquareKanban,
  UserRound,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { TextWithLinks } from '@/components/ui/text-with-links'
import { PageSkeleton } from '@/components/ui/page-skeleton'
import { FormField } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useAuth } from '@/contexts/AuthContext'
import { cn } from '@/lib/utils'
import {
  formatDaysPassed,
  formatDueDate,
  formatTimeSpent,
  ticketDelay,
  todayISO,
  type ColumnId,
  type Person,
  type Ticket,
  type TicketProject,
} from '@/components/tickets/data'
import { ApiError } from '@/services/api'
import {
  createTicket,
  deleteTicket as deleteTicketRequest,
  fetchTicketBoard,
  reorderTickets,
  updateTicket,
  type TicketPayload,
} from '@/services/tickets'

interface TicketsSearch {
  ticket?: string
}

export const Route = createFileRoute('/tickets')({
  component: TicketsPage,
  validateSearch: (search: Record<string, unknown>): TicketsSearch => {
    const raw = search.ticket
    const ticket = typeof raw === 'string' || typeof raw === 'number' ? String(raw).trim() : ''
    return ticket ? { ticket } : {}
  },
})

function ticketLink(ticketId: string): string {
  return `${window.location.origin}/tickets?ticket=${encodeURIComponent(ticketId)}`
}

async function copyText(value: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value)
      return true
    }
  } catch {
    // On retombe sur la copie manuelle ci-dessous.
  }
  const area = document.createElement('textarea')
  area.value = value
  area.setAttribute('readonly', '')
  area.style.position = 'fixed'
  area.style.opacity = '0'
  document.body.appendChild(area)
  area.select()
  const ok = document.execCommand('copy')
  document.body.removeChild(area)
  return ok
}

const COLUMNS: Array<{
  id: ColumnId
  label: string
  dot: string
  count: string
  surface: string
}> = [
  {
    id: 'todo',
    label: 'À faire',
    dot: 'bg-slate-500',
    count: 'bg-white text-slate-600 shadow-sm',
    surface: 'border-slate-200/80 bg-white shadow-sm',
  },
  {
    id: 'doing',
    label: 'En cours',
    dot: 'bg-blue-500',
    count: 'bg-white text-blue-600 shadow-sm',
    surface: 'border-blue-200/80 bg-[#f5f8ff] shadow-sm',
  },
  {
    id: 'review',
    label: 'En révision',
    dot: 'bg-amber-500',
    count: 'bg-white text-amber-700 shadow-sm',
    surface: 'border-amber-200/80 bg-[#fff8ec] shadow-sm',
  },
  {
    id: 'done',
    label: 'Fait',
    dot: 'bg-emerald-500',
    count: 'bg-white text-emerald-700 shadow-sm',
    surface: 'border-emerald-200/80 bg-[#f2fbf6] shadow-sm',
  },
]

const COLUMN_IDS = COLUMNS.map((column) => column.id)

type ProjectScope = 'externe' | 'interne'

interface TicketDraft {
  title: string
  description: string
  projectScope: ProjectScope
  projectId: string
  projectName: string
  cpIds: string[]
  assigneeIds: string[]
  dueDate: string
  status: ColumnId
}

function projectsForScope(projects: TicketProject[], scope: ProjectScope) {
  return projects.filter((project) => project.visibility === scope)
}

function blankDraft(): TicketDraft {
  return {
    title: '',
    description: '',
    projectScope: 'externe',
    projectId: '',
    projectName: '',
    cpIds: [],
    assigneeIds: [],
    dueDate: todayISO(),
    status: 'todo',
  }
}

function draftForCreate(projects: TicketProject[]): TicketDraft {
  const project = projectsForScope(projects, 'externe')[0]
  return {
    ...blankDraft(),
    projectId: project?.id ?? '',
    cpIds: project?.managerIds ?? [],
    assigneeIds: project?.developerIds ?? [],
  }
}

function needsTimePrompt(next: ColumnId, previous?: ColumnId): boolean {
  return (next === 'review' || next === 'done') && previous !== next
}

function findPerson(people: Person[], id: string) {
  return people.find((person) => person.id === id)
}

function findProject(projects: TicketProject[], id: string) {
  return projects.find((project) => project.id === id)
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message
  return 'Une erreur est survenue.'
}

function draftFromTicket(ticket: Ticket, projects: TicketProject[]): TicketDraft {
  const project = findProject(projects, ticket.projectId)
  return {
    title: ticket.title,
    description: ticket.description,
    projectScope: project?.visibility === 'interne' ? 'interne' : 'externe',
    projectId: ticket.projectId,
    projectName: '',
    cpIds: ticket.cpIds,
    assigneeIds: ticket.assigneeIds,
    dueDate: ticket.dueDate,
    status: ticket.status,
  }
}

function isColumnId(value: string): value is ColumnId {
  return COLUMN_IDS.includes(value as ColumnId)
}

function ticketsInColumn(tickets: Ticket[], status: ColumnId) {
  return tickets.filter((ticket) => ticket.status === status).sort((a, b) => a.order - b.order)
}

function moveTicket(tickets: Ticket[], activeId: string, overId: string): Ticket[] {
  const active = tickets.find((ticket) => ticket.id === activeId)
  if (!active) return tickets

  const overTicket = tickets.find((ticket) => ticket.id === overId)
  const targetStatus: ColumnId | undefined = isColumnId(overId) ? overId : overTicket?.status
  if (!targetStatus) return tickets

  const source = ticketsInColumn(tickets, active.status)

  if (active.status === targetStatus) {
    if (!overTicket) return tickets
    const oldIndex = source.findIndex((ticket) => ticket.id === activeId)
    const newIndex = source.findIndex((ticket) => ticket.id === overId)
    if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return tickets
    const reordered = arrayMove(source, oldIndex, newIndex).map((ticket, index) => ({
      ...ticket,
      order: index,
    }))
    const byId = new Map(reordered.map((ticket) => [ticket.id, ticket]))
    return tickets.map((ticket) => byId.get(ticket.id) ?? ticket)
  }

  const target = ticketsInColumn(tickets, targetStatus)
  const insertAt = overTicket ? target.findIndex((ticket) => ticket.id === overId) : target.length
  const moved: Ticket = {
    ...active,
    status: targetStatus,
    doneAt: targetStatus === 'done' ? active.doneAt ?? todayISO() : null,
    reviewedAt: targetStatus === 'review' ? active.reviewedAt ?? todayISO() : active.reviewedAt,
  }
  const nextTarget = [...target]
  nextTarget.splice(insertAt < 0 ? nextTarget.length : insertAt, 0, moved)
  const orders = new Map(nextTarget.map((ticket, index) => [ticket.id, index]))

  return tickets
    .filter((ticket) => ticket.id !== activeId)
    .map((ticket) => {
      const order = orders.get(ticket.id)
      return order === undefined ? ticket : { ...ticket, order }
    })
    .concat([{ ...moved, order: orders.get(moved.id) ?? nextTarget.length - 1 }])
}

function PeopleMultiSelect({
  people,
  selectedIds,
  onToggle,
  loading,
  multiple = true,
  loadingLabel = 'Chargement des développeurs…',
  placeholder = 'Sélectionner des développeurs',
  searchPlaceholder = 'Rechercher un développeur...',
  emptyLabel = 'Aucun développeur',
}: {
  people: Person[]
  selectedIds: string[]
  onToggle: (id: string) => void
  loading: boolean
  multiple?: boolean
  loadingLabel?: string
  placeholder?: string
  searchPlaceholder?: string
  emptyLabel?: string
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const rootRef = useDismiss(open, () => {
    setOpen(false)
    setSearch('')
  })
  const selected = people.filter((person) => selectedIds.includes(person.id))
  const visible = people.filter((person) => person.name.toLowerCase().includes(search.trim().toLowerCase()))
  const label = loading
    ? loadingLabel
    : selected.length === 0
      ? placeholder
      : selected.map((person) => person.name).join(', ')

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        disabled={loading}
        onClick={() => setOpen((current) => !current)}
        className="flex h-10 w-full items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 text-left text-sm disabled:opacity-60"
      >
        <span className={cn('min-w-0 truncate', selected.length === 0 && 'text-gray-400')}>{label}</span>
        <ChevronDown className={cn('h-4 w-4 shrink-0 text-gray-400', open && 'rotate-180')} />
      </button>
      {open ? (
        <div className="absolute bottom-full z-50 mb-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
          <div className="border-b border-gray-100 p-2">
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={searchPlaceholder}
              className="h-9 w-full rounded-md border border-gray-200 bg-gray-50 px-3 text-sm outline-none focus:ring-2 focus:ring-cyan-400"
            />
          </div>
          <div className="max-h-60 overflow-auto py-1">
            {visible.length === 0 ? (
              <p className="px-3 py-2 text-center text-sm text-gray-400">{emptyLabel}</p>
            ) : (
              visible.map((person) => {
                const checked = selectedIds.includes(person.id)
                return (
                  <button
                    key={person.id}
                    type="button"
                    onClick={() => {
                      onToggle(person.id)
                      if (!multiple) {
                        setOpen(false)
                        setSearch('')
                      }
                    }}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50',
                      checked && 'bg-[#f3fbf9]'
                    )}
                  >
                    <span
                      className="flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold text-white"
                      style={{ backgroundColor: person.color }}
                    >
                      {person.initial}
                    </span>
                    <span className="truncate">{person.name}</span>
                    {checked ? <Check className="ml-auto h-4 w-4 text-[#14c4b0]" /> : null}
                  </button>
                )
              })
            )}
          </div>
        </div>
      ) : null}
    </div>
  )
}

const HOURS_PER_DAY = 8
const DAY_PRESETS = [0.25, 0.5, 1, 2, 3]
const HOUR_PRESETS = [1, 2, 4, 8, 16]

type TimeUnit = 'days' | 'hours'

interface TimePrompt {
  title: string
  ticketId: string | null
  targetStatus: 'review' | 'done'
  previousTickets: Ticket[] | null
  changed: Array<{ id: string; status: ColumnId; order: number }> | null
  timeOnly?: boolean
}

interface TimeDraft {
  unit: TimeUnit
  value: string
}

function parseAmount(custom: string, choice: number | null): number | null {
  const typed = custom.trim().replace(',', '.')
  if (typed) {
    const value = Number(typed)
    return Number.isFinite(value) && value > 0 ? value : null
  }
  return choice
}

function toSpentDays(custom: string, choice: number | null, unit: TimeUnit): number | null {
  const amount = parseAmount(custom, choice)
  if (!amount) return null
  const days = unit === 'hours' ? amount / HOURS_PER_DAY : amount
  if (!Number.isFinite(days) || days <= 0) return null
  return Math.round(days * 10000) / 10000
}

function amountToInput(days: number | null | undefined, unit: TimeUnit): string {
  if (days == null || days <= 0) return ''
  const amount = unit === 'hours' ? days * HOURS_PER_DAY : days
  const rounded = Math.round(amount * 10000) / 10000
  return String(rounded)
}

function convertDisplayed(value: string, from: TimeUnit, to: TimeUnit): string {
  if (from === to) return value
  const amount = Number(value.trim().replace(',', '.'))
  if (!value.trim() || !Number.isFinite(amount) || amount <= 0) return ''
  const days = from === 'hours' ? amount / HOURS_PER_DAY : amount
  const next = to === 'hours' ? days * HOURS_PER_DAY : days
  return String(Math.round(next * 10000) / 10000)
}

function storedTimeDraft(days: number | null | undefined, unit?: 'days' | 'hours' | null): TimeDraft {
  const next: TimeUnit = unit === 'hours' ? 'hours' : 'days'
  return { unit: next, value: amountToInput(days, next) }
}

function personNameById(people: Person[], id: string | null | undefined): string | null {
  if (!id) return null
  const name = people.find((person) => person.id === id)?.name?.trim()
  return name || null
}

function timeRoleTitle(role: string, names: Array<string | null | undefined>): string {
  const labeled = names.map((name) => name?.trim()).filter((name): name is string => Boolean(name))
  if (labeled.length === 0) return role
  return `${role} (${labeled.join(', ')})`
}

function canManageCpTime(
  userId: string | undefined,
  role: string | undefined,
  chefIds: string[] | null | undefined
): boolean {
  if (!userId) return false
  if (role === 'super_admin') return true
  return Boolean(chefIds?.includes(userId))
}

function TimeEntryBlock({
  title,
  optional,
  draft,
  onChange,
  error,
}: {
  title: string
  optional?: boolean
  draft: TimeDraft
  onChange: (next: TimeDraft) => void
  error?: string
}) {
  const presets = draft.unit === 'hours' ? HOUR_PRESETS : DAY_PRESETS
  return (
    <div className="rounded-xl border border-gray-200 p-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-gray-900">{title}</p>
        {optional ? <span className="text-xs text-gray-400">Facultatif</span> : null}
      </div>
      <div className="mt-3 inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white p-1">
        <button
          type="button"
          onClick={() =>
            onChange({
              unit: 'days',
              value: draft.unit === 'days' ? draft.value : convertDisplayed(draft.value, draft.unit, 'days'),
            })
          }
          className={cn(
            'rounded-full px-3 py-1 text-sm font-medium',
            draft.unit === 'days' ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:text-gray-800'
          )}
        >
          Jours
        </button>
        <button
          type="button"
          onClick={() =>
            onChange({
              unit: 'hours',
              value: draft.unit === 'hours' ? draft.value : convertDisplayed(draft.value, draft.unit, 'hours'),
            })
          }
          className={cn(
            'rounded-full px-3 py-1 text-sm font-medium',
            draft.unit === 'hours' ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:text-gray-800'
          )}
        >
          Heures
        </button>
      </div>
      <div className="mt-3 flex gap-2">
        {presets.map((preset) => {
          const selected = draft.value.trim().replace(',', '.') === String(preset)
          return (
            <button
              key={preset}
              type="button"
              onClick={() => onChange({ ...draft, value: String(preset) })}
              className={cn(
                'inline-flex h-10 flex-1 items-center justify-center rounded-md border px-2 text-sm font-medium transition-colors',
                selected
                  ? 'border-transparent bg-gradient-to-r from-cyan-400 to-cyan-500 text-white shadow-md'
                  : 'border-gray-200 bg-white text-gray-700 shadow-sm hover:bg-gray-50'
              )}
            >
              {draft.unit === 'hours' ? `${preset}h` : `${preset}j`}
            </button>
          )
        })}
      </div>
      <FormField
        label={draft.unit === 'hours' ? 'Autre durée (en heures)' : 'Autre durée (en jours)'}
        className="mt-4"
        error={error}
      >
        <Input
          type="number"
          min={draft.unit === 'hours' ? '0.5' : '0.25'}
          step={draft.unit === 'hours' ? '0.5' : '0.25'}
          value={draft.value}
          onChange={(event) => onChange({ ...draft, value: event.target.value })}
          placeholder={draft.unit === 'hours' ? 'Ex: 4' : 'Ex: 1.5'}
          className="border-gray-200 bg-gray-50 focus:border-transparent focus:ring-cyan-400"
        />
      </FormField>
    </div>
  )
}

function TicketsPage() {
  const { user } = useAuth()
  const { ticket: ticketParam } = Route.useSearch()
  const navigate = useNavigate({ from: '/tickets' })
  const [tickets, setTickets] = useState<Ticket[]>([])
  const [projects, setProjects] = useState<TicketProject[]>([])
  const [chefs, setChefs] = useState<Person[]>([])
  const [devs, setDevs] = useState<Person[]>([])
  const [loading, setLoading] = useState(true)
  const [pageError, setPageError] = useState('')
  const [saving, setSaving] = useState(false)
  const [projectFilter, setProjectFilter] = useState('all')
  const [cpFilter, setCpFilter] = useState('all')
  const [devFilter, setDevFilter] = useState('all')
  const [query, setQuery] = useState('')
  const [activeId, setActiveId] = useState<string | null>(null)
  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  // Mode/ticket affichés par la modale : conservés pendant l'animation de fermeture,
  // sinon le contenu bascule en « Nouveau ticket » et les éléments se déplacent.
  const [shownMode, setShownMode] = useState(dialogMode)
  const [shownEditingId, setShownEditingId] = useState(editingId)
  if (dialogMode && (dialogMode !== shownMode || editingId !== shownEditingId)) {
    setShownMode(dialogMode)
    setShownEditingId(editingId)
  }
  const [draft, setDraft] = useState<TicketDraft>(blankDraft)
  const [formError, setFormError] = useState('')
  const [timePrompt, setTimePrompt] = useState<TimePrompt | null>(null)
  const [devTime, setDevTime] = useState<TimeDraft>({ unit: 'days', value: '' })
  const [cpTime, setCpTime] = useState<TimeDraft>({ unit: 'days', value: '' })
  const [timeError, setTimeError] = useState('')
  const [linkCopied, setLinkCopied] = useState(false)
  const skipCardClick = useRef(false)
  const ticketsRef = useRef(tickets)
  const openedFromUrl = useRef<string | null>(null)
  ticketsRef.current = tickets

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  )

  const people = useMemo(() => {
    const byId = new Map<string, Person>()
    for (const person of [...chefs, ...devs]) byId.set(person.id, person)
    return [...byId.values()]
  }, [chefs, devs])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    fetchTicketBoard()
      .then((board) => {
        if (cancelled) return
        setTickets(board.tickets)
        setProjects(board.projects)
        setChefs(board.chefs)
        setDevs(board.devs)
        setPageError('')
      })
      .catch((error: unknown) => {
        if (!cancelled) setPageError(errorMessage(error))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Rafraîchissement auto (changements des autres utilisateurs / MCP), sauté pendant une action en cours
  const busyRef = useRef(false)
  busyRef.current = Boolean(dialogMode || timePrompt || activeId || saving)
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (busyRef.current || document.hidden) return
      fetchTicketBoard()
        .then((board) => {
          if (busyRef.current) return
          setTickets(board.tickets)
          setProjects(board.projects)
          setChefs(board.chefs)
          setDevs(board.devs)
        })
        .catch(() => undefined)
    }, 30_000)
    return () => window.clearInterval(timer)
  }, [])

  const filteredTickets = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return tickets.filter((ticket) => {
      if (projectFilter !== 'all' && ticket.projectId !== projectFilter) return false
      if (cpFilter !== 'all' && !ticket.cpIds.includes(cpFilter)) return false
      if (devFilter !== 'all' && !ticket.assigneeIds.includes(devFilter)) return false
      if (!needle) return true
      const projectName = findProject(projects, ticket.projectId)?.name ?? ''
      return (
        ticket.title.toLowerCase().includes(needle) ||
        ticket.description.toLowerCase().includes(needle) ||
        projectName.toLowerCase().includes(needle)
      )
    })
  }, [tickets, projects, projectFilter, cpFilter, devFilter, query])

  const activeTicket = tickets.find((ticket) => ticket.id === activeId) ?? null
  const ticketLabel = loading
    ? 'Chargement…'
    : filteredTickets.length === 1
      ? '1 ticket'
      : `${filteredTickets.length} tickets`

  const timePromptLabels = useMemo(() => {
    if (!timePrompt) {
      return { cpTitle: 'Chef de projet', devTitle: 'Développeur', canManageCp: false }
    }
    const ticket =
      (timePrompt.ticketId
        ? tickets.find((item) => item.id === timePrompt.ticketId) ??
          timePrompt.previousTickets?.find((item) => item.id === timePrompt.ticketId)
        : null) ?? null
    const projectId = ticket?.projectId || (dialogMode ? draft.projectId : '')
    const project = findProject(projects, projectId)
    const cpIds =
      (ticket?.cpIds?.length ? ticket.cpIds : null) ||
      (draft.cpIds.length ? draft.cpIds : null) ||
      project?.managerIds ||
      []
    const assigneeIds =
      (ticket?.assigneeIds?.length ? ticket.assigneeIds : null) ||
      (draft.assigneeIds.length ? draft.assigneeIds : null) ||
      project?.developerIds ||
      []
    return {
      cpIds,
      canManageCp: canManageCpTime(user?.id, user?.role, cpIds),
      cpTitle: timeRoleTitle(
        'Chef de projet',
        cpIds.map((id) => personNameById(people, id))
      ),
      devTitle: timeRoleTitle(
        'Développeur',
        assigneeIds.map((id) => personNameById(people, id))
      ),
    }
  }, [timePrompt, tickets, dialogMode, draft.projectId, draft.cpIds, draft.assigneeIds, projects, people, user?.id, user?.role])

  const openCreate = () => {
    setDialogMode('create')
    setEditingId(null)
    setDraft(draftForCreate(projects))
    setFormError('')
  }

  const showTicket = (ticket: Ticket) => {
    setDialogMode('edit')
    setEditingId(ticket.id)
    setDraft(draftFromTicket(ticket, projects))
    setFormError('')
    setLinkCopied(false)
  }

  const openEdit = (ticket: Ticket) => {
    if (skipCardClick.current) return
    showTicket(ticket)
    openedFromUrl.current = ticket.id
    void navigate({ search: { ticket: ticket.id }, replace: true })
  }

  const closeDialog = () => {
    setDialogMode(null)
    setEditingId(null)
    setFormError('')
    if (ticketParam) void navigate({ search: {}, replace: true })
  }

  // Lien direct /tickets?ticket=ID : ouvre le ticket dès que le board est chargé.
  // openedFromUrl n'est remis à zéro qu'une fois le paramètre réellement retiré de l'URL :
  // navigate() est asynchrone, et le remettre à zéro dans closeDialog rouvrait la modale
  // dès que `tickets` changeait (ex. après un enregistrement).
  useEffect(() => {
    if (!ticketParam) {
      openedFromUrl.current = null
      return
    }
    if (loading || openedFromUrl.current === ticketParam) return
    openedFromUrl.current = ticketParam
    const ticket = tickets.find((item) => item.id === ticketParam)
    if (ticket) {
      showTicket(ticket)
      return
    }
    setPageError('Ticket introuvable : il a peut-être été supprimé ou vous n’y avez pas accès.')
    void navigate({ search: {}, replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, ticketParam, tickets])

  const copyTicketLink = () => {
    if (!editingId) return
    void copyText(ticketLink(editingId)).then((ok) => {
      if (!ok) {
        setFormError('Copie impossible depuis ce navigateur.')
        return
      }
      setLinkCopied(true)
      window.setTimeout(() => setLinkCopied(false), 2000)
    })
  }

  const handleDragStart = (event: DragStartEvent) => {
    skipCardClick.current = true
    setActiveId(String(event.active.id))
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    setActiveId(null)
    window.setTimeout(() => {
      skipCardClick.current = false
    }, 0)
    if (!over) return
    const previous = ticketsRef.current
    const next = moveTicket(previous, String(active.id), String(over.id))
    if (next === previous) return
    const changed = next.filter((ticket) => {
      const before = previous.find((item) => item.id === ticket.id)
      return !before || before.status !== ticket.status || before.order !== ticket.order
    })
    const timed = changed.find((ticket) => {
      const before = previous.find((item) => item.id === ticket.id)
      return before && needsTimePrompt(ticket.status, before.status)
    })
    if (timed && (timed.status === 'review' || timed.status === 'done')) {
      const source = previous.find((item) => item.id === timed.id)
      setTickets(next)
      resetTimeFields(source?.timeSpentDays, source?.timeSpentUnit, source?.cpTimeSpentDays, source?.cpTimeSpentUnit)
      setTimePrompt({
        title: timed.title,
        ticketId: timed.id,
        targetStatus: timed.status,
        previousTickets: previous,
        changed: changed.map((ticket) => ({ id: ticket.id, status: ticket.status, order: ticket.order })),
      })
      return
    }
    setTickets(next)
    void reorderTickets(changed.map((ticket) => ({ id: ticket.id, status: ticket.status, order: ticket.order })))
      .then((saved) => {
        const savedById = new Map(saved.map((ticket) => [ticket.id, ticket]))
        setTickets((current) => current.map((ticket) => savedById.get(ticket.id) ?? ticket))
      })
      .catch((error: unknown) => {
        setTickets(previous)
        setPageError(errorMessage(error))
      })
  }

  const toggleAssignee = (personId: string) => {
    setDraft((current) => ({
      ...current,
      assigneeIds: current.assigneeIds.includes(personId)
        ? current.assigneeIds.filter((id) => id !== personId)
        : [...current.assigneeIds, personId],
    }))
  }

  const ticketPayload = (): TicketPayload => {
    const projectName = draft.projectScope === 'interne' ? draft.projectName.trim() : ''
    return {
      title: draft.title.trim(),
      description: draft.description.trim(),
      projectId: projectName ? '' : draft.projectId,
      projectName,
      cpIds: draft.cpIds,
      assigneeIds: draft.assigneeIds,
      dueDate: draft.dueDate,
      status: draft.status,
    }
  }

  const refreshProjects = () => {
    void fetchTicketBoard()
      .then((board) => setProjects(board.projects))
      .catch(() => undefined)
  }

  const saveTicket = () => {
    const title = draft.title.trim()
    if (!title) {
      setFormError('Le titre est obligatoire.')
      return
    }
    const typedName = draft.projectScope === 'interne' ? draft.projectName.trim() : ''
    if (!draft.projectId && !typedName) {
      setFormError(
        draft.projectScope === 'interne'
          ? 'Choisissez un projet interne ou écrivez son nom.'
          : 'Le projet est obligatoire.'
      )
      return
    }

    const existing = editingId ? tickets.find((ticket) => ticket.id === editingId) : undefined
    if (needsTimePrompt(draft.status, existing?.status) && (draft.status === 'review' || draft.status === 'done')) {
      resetTimeFields(existing?.timeSpentDays, existing?.timeSpentUnit, existing?.cpTimeSpentDays, existing?.cpTimeSpentUnit)
      setTimePrompt({
        title,
        ticketId: editingId,
        targetStatus: draft.status,
        previousTickets: null,
        changed: null,
      })
      return
    }

    const payload = ticketPayload()
    setSaving(true)
    setFormError('')
    const request =
      dialogMode === 'edit' && editingId
        ? updateTicket(editingId, payload).then((saved) => {
            setTickets((current) => current.map((ticket) => (ticket.id === saved.id ? saved : ticket)))
          })
        : createTicket(payload).then((saved) => {
            setTickets((current) => [...current, saved])
          })

    void request
      .then(() => {
        if (payload.projectName) refreshProjects()
        closeDialog()
      })
      .catch((error: unknown) => setFormError(errorMessage(error)))
      .finally(() => setSaving(false))
  }

  const resetTimeFields = (
    devDays: number | null | undefined,
    devUnit?: 'days' | 'hours' | null,
    cpDays?: number | null,
    cpUnit?: 'days' | 'hours' | null
  ) => {
    setDevTime(storedTimeDraft(devDays, devUnit))
    setCpTime(storedTimeDraft(cpDays, cpUnit))
    setTimeError('')
  }

  const openEditTime = (ticket: Ticket) => {
    resetTimeFields(ticket.timeSpentDays, ticket.timeSpentUnit, ticket.cpTimeSpentDays, ticket.cpTimeSpentUnit)
    setTimePrompt({
      title: ticket.title,
      ticketId: ticket.id,
      targetStatus: ticket.status === 'review' ? 'review' : 'done',
      previousTickets: null,
      changed: null,
      timeOnly: true,
    })
  }

  const closeTimePrompt = () => {
    if (timePrompt?.previousTickets) setTickets(timePrompt.previousTickets)
    setTimePrompt(null)
    setTimeError('')
  }

  const confirmTime = () => {
    if (!timePrompt) return
    const days = toSpentDays(devTime.value, null, devTime.unit)
    const cpDays = toSpentDays(cpTime.value, null, cpTime.unit)
    const canCp = timePromptLabels.canManageCp
    if (canCp) {
      if (!days && !cpDays) {
        setTimeError('Indiquez le temps développeur et/ou chef de projet.')
        return
      }
    } else if (!days) {
      setTimeError('Indiquez votre temps.')
      return
    }
    const timeFields = {
      ...(days
        ? { timeSpentDays: days, timeSpentUnit: devTime.unit as 'days' | 'hours' }
        : {}),
      ...(canCp
        ? {
            cpTimeSpentDays: cpDays,
            cpTimeSpentUnit: cpDays ? cpTime.unit : null,
            cpTimeTouched: true as const,
          }
        : {}),
    }
    setSaving(true)
    setTimeError('')
    const applySaved = (savedList: Ticket[]) => {
      const savedById = new Map(savedList.map((ticket) => [ticket.id, ticket]))
      setTickets((current) => current.map((ticket) => savedById.get(ticket.id) ?? ticket))
    }
    let request: Promise<void>
    if (timePrompt.timeOnly && timePrompt.ticketId) {
      const ticket = ticketsRef.current.find((item) => item.id === timePrompt.ticketId)
      if (!ticket) {
        setSaving(false)
        setTimeError('Ticket introuvable.')
        return
      }
      request = updateTicket(ticket.id, {
        title: ticket.title,
        description: ticket.description,
        projectId: ticket.projectId,
        cpIds: ticket.cpIds,
        assigneeIds: ticket.assigneeIds,
        dueDate: ticket.dueDate,
        status: ticket.status,
        ...timeFields,
      }).then((saved) => applySaved([saved]))
    } else if (timePrompt.changed) {
      request = reorderTickets(
        timePrompt.changed.map((item) => {
          const timed = item.id === timePrompt.ticketId && (item.status === 'review' || item.status === 'done')
          return {
            ...item,
            ...(timed ? timeFields : {}),
          }
        })
      ).then(applySaved)
    } else if (dialogMode === 'edit' && editingId) {
      request = updateTicket(editingId, {
        ...ticketPayload(),
        ...timeFields,
      }).then((saved) => {
        applySaved([saved])
        if (draft.projectScope === 'interne' && draft.projectName.trim()) refreshProjects()
        closeDialog()
      })
    } else {
      request = createTicket({
        ...ticketPayload(),
        ...timeFields,
      }).then((saved) => {
        setTickets((current) => [...current, saved])
        if (draft.projectScope === 'interne' && draft.projectName.trim()) refreshProjects()
        closeDialog()
      })
    }

    void request
      .then(() => setTimePrompt(null))
      .catch((error: unknown) => {
        setTimeError(errorMessage(error))
        if (timePrompt.previousTickets) setTickets(timePrompt.previousTickets)
      })
      .finally(() => setSaving(false))
  }

  const deleteTicket = () => {
    if (!editingId) return
    const id = editingId
    setSaving(true)
    void deleteTicketRequest(id)
      .then(() => {
        setTickets((current) => current.filter((ticket) => ticket.id !== id))
        closeDialog()
      })
      .catch((error: unknown) => setFormError(errorMessage(error)))
      .finally(() => setSaving(false))
  }

  return (
    <div className="min-h-screen">
      <div className="px-4 py-6 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-4">
          <div className="flex shrink-0 items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#e7f8f5]">
              <SquareKanban className="h-5 w-5 text-[#12b5a0]" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-gray-900">Tickets</h1>
              <p className="text-sm text-gray-500">{ticketLabel}</p>
            </div>
          </div>
          <div className="flex min-w-0 items-center justify-end gap-2">
            <TicketFilterBar
              projects={projects}
              chefs={chefs}
              devs={devs}
              query={query}
              onQueryChange={setQuery}
              projectFilter={projectFilter}
              cpFilter={cpFilter}
              devFilter={devFilter}
              onProjectChange={setProjectFilter}
              onCpChange={setCpFilter}
              onDevChange={setDevFilter}
            />
            <Button
              type="button"
              onClick={openCreate}
              className="h-9 shrink-0 bg-gradient-to-r from-cyan-400 to-cyan-500 px-4 text-white shadow-md hover:from-cyan-500 hover:to-cyan-600"
            >
              <Plus className="mr-1.5 h-4 w-4" />
              Nouveau ticket
            </Button>
          </div>
        </div>

        {pageError ? <p className="mt-4 text-sm text-rose-600">{pageError}</p> : null}

        {loading ? (
          <PageSkeleton variant="board" />
        ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={() => {
            setActiveId(null)
            window.setTimeout(() => {
              skipCardClick.current = false
            }, 0)
          }}
        >
          <div className="mt-5 flex items-start gap-4 overflow-x-auto pb-4 [scrollbar-gutter:stable]">
            {COLUMNS.map((column) => (
              <KanbanColumn
                key={column.id}
                column={column}
                tickets={ticketsInColumn(filteredTickets, column.id)}
                projects={projects}
                people={people}
                currentUserId={user?.id}
                currentUserRole={user?.role}
                boardDragging={activeId !== null}
                onOpen={openEdit}
              />
            ))}
          </div>
          <DragOverlay>
            {activeTicket ? (
              <TicketCard
                ticket={activeTicket}
                projects={projects}
                people={people}
                currentUserId={user?.id}
                currentUserRole={user?.role}
                overlay
              />
            ) : null}
          </DragOverlay>
        </DndContext>
        )}
      </div>

      <Modal
open={dialogMode !== null}
onOpenChange={(open) => !open && closeDialog()}
hideHeader
showCancel={false}
size="md"
footer={
  <div className="flex w-full items-center justify-between gap-2">
    <div className="flex gap-2">
      {shownMode === 'edit' ? (
        <Button type="button" variant="outline" onClick={deleteTicket} disabled={saving} className="text-red-600">
          Supprimer
        </Button>
      ) : null}
      {shownMode === 'edit' &&
      ['review', 'done'].includes(tickets.find((ticket) => ticket.id === shownEditingId)?.status ?? '') ? (
        <Button
          type="button"
          variant="outline"
          disabled={saving}
          onClick={() => {
            const ticket = tickets.find((item) => item.id === shownEditingId)
            if (!ticket) return
            closeDialog()
            openEditTime(ticket)
          }}
        >
          Modifier le temps
        </Button>
      ) : null}
    </div>
    <div className="flex gap-2">
      <Button type="button" variant="outline" onClick={closeDialog}>
        Annuler
      </Button>
      <Button
        type="button"
        onClick={saveTicket}
        disabled={saving}
        className="bg-gradient-to-r from-cyan-400 to-cyan-500 text-white shadow-md hover:from-cyan-500 hover:to-cyan-600"
      >
        {saving ? 'Enregistrement…' : shownMode === 'edit' ? 'Enregistrer' : 'Créer le ticket'}
      </Button>
    </div>
  </div>
}
>
<div className="border-b border-gray-200 px-6 py-4 -mx-6 -mt-6 mb-2 flex items-start justify-between gap-3">
  <div>
    <h2 className="text-2xl font-bold text-gray-800">
      {shownMode === 'edit' ? 'Modifier le ticket' : 'Nouveau ticket'}
    </h2>
    <p className="mt-1 text-sm text-gray-500">
      {shownMode === 'edit'
        ? 'Mettez à jour le ticket et sa colonne'
        : 'Ajoutez un ticket sur le tableau'}
    </p>
  </div>
  {shownMode === 'edit' ? (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={copyTicketLink}
      title="Copier le lien direct vers ce ticket"
      className="mt-1 shrink-0 gap-1.5"
    >
      {linkCopied ? <Check className="h-4 w-4 text-[#14c4b0]" /> : <Link2 className="h-4 w-4" />}
      {linkCopied ? 'Lien copié' : 'Copier le lien'}
    </Button>
  ) : null}
</div>

<div className="grid grid-cols-1 gap-4 md:grid-cols-2 [&>*]:min-w-0">
  <FormField label="Projet" required className="md:col-span-2">
    <div className="space-y-3">
      <div className="inline-flex rounded-full border border-gray-200 bg-gray-50 p-1">
        {(['externe', 'interne'] as const).map((scope) => (
          <button
            key={scope}
            type="button"
            onClick={() => {
              if (scope === draft.projectScope) return
              if (scope === 'externe') {
                const project = projectsForScope(projects, 'externe')[0]
                setDraft((current) => ({
                  ...current,
                  projectScope: 'externe',
                  projectName: '',
                  projectId: project?.id ?? '',
                  cpIds: project?.managerIds ?? current.cpIds,
                  assigneeIds: project ? project.developerIds : current.assigneeIds,
                }))
                return
              }
              setDraft((current) => ({
                ...current,
                projectScope: 'interne',
                projectName: '',
                projectId: '',
              }))
            }}
            className={`rounded-full px-3 py-1 text-sm font-medium ${
              draft.projectScope === scope
                ? 'bg-white text-gray-950 shadow-sm'
                : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            {scope === 'externe' ? 'Externe' : 'Interne'}
          </button>
        ))}
      </div>
      <SearchableSelect
        value={draft.projectId}
        onChange={(value) => {
          const project = findProject(projects, value)
          setDraft((current) => ({
            ...current,
            projectId: value,
            projectName: '',
            cpIds: project?.managerIds ?? [],
            assigneeIds: project?.developerIds ?? [],
          }))
        }}
        options={projectsForScope(projects, draft.projectScope).map((project) => ({
          value: project.id,
          label: project.name,
        }))}
        placeholder={
          loading
            ? 'Chargement des projets…'
            : draft.projectScope === 'interne'
              ? 'Sélectionner un projet interne'
              : 'Sélectionner un projet'
        }
        searchPlaceholder="Rechercher un projet..."
        disabled={loading}
        className="border-gray-200 bg-gray-50"
      />
      {draft.projectScope === 'interne' ? (
        <Input
          value={draft.projectName}
          onChange={(event) =>
            setDraft((current) => ({
              ...current,
              projectName: event.target.value,
              projectId: event.target.value.trim() ? '' : current.projectId,
            }))
          }
          placeholder="Ou écrire un nouveau projet interne"
          className="border-gray-200 bg-gray-50 focus:border-transparent focus:ring-cyan-400"
        />
      ) : null}
    </div>
  </FormField>
  <FormField label="Statut" className="md:col-span-2">
    <Select
      id="ticket-status"
      value={draft.status}
      onChange={(event) =>
        setDraft((current) => ({ ...current, status: event.target.value as ColumnId }))
      }
      options={COLUMNS.map((column) => ({ value: column.id, label: column.label }))}
      className="border-gray-200 bg-gray-50 focus:border-transparent focus:ring-cyan-400"
    />
  </FormField>
  <FormField label="Titre" required className="md:col-span-2" error={formError || undefined}>
    <Input
      id="ticket-title"
      value={draft.title}
      onChange={(event) => setDraft((current) => ({ ...current, title: event.target.value }))}
      placeholder="Ex: Corriger le timeout des requêtes longues"
      className="border-gray-200 bg-gray-50 focus:border-transparent focus:ring-cyan-400"
    />
  </FormField>
  <FormField label="Description" className="md:col-span-2">
    <Textarea
      id="ticket-description"
      value={draft.description}
      onChange={(event) =>
        setDraft((current) => ({ ...current, description: event.target.value }))
      }
      placeholder="Contexte, objectif, critère d'acceptation"
      rows={3}
      className="resize-none border-gray-200 bg-gray-50 focus:border-transparent focus:ring-cyan-400"
    />
  </FormField>
  <FormField label="Chefs de projet">
    <PeopleMultiSelect
      people={devs}
      selectedIds={draft.cpIds}
      onToggle={(id) =>
        setDraft((current) => ({
          ...current,
          cpIds: current.cpIds.includes(id)
            ? current.cpIds.filter((item) => item !== id)
            : [...current.cpIds, id],
        }))
      }
      loading={loading}
      loadingLabel="Chargement des chefs de projet…"
      placeholder="Sélectionner un ou plusieurs chefs de projet"
      searchPlaceholder="Rechercher un chef de projet..."
      emptyLabel="Aucun chef de projet"
    />
  </FormField>
  <FormField label="Échéance">
    <Input
      id="ticket-due"
      type="date"
      value={draft.dueDate}
      onChange={(event) => setDraft((current) => ({ ...current, dueDate: event.target.value }))}
      className="border-gray-200 bg-gray-50 focus:border-transparent focus:ring-cyan-400"
    />
  </FormField>
  <FormField label="Développeurs" className="md:col-span-2">
    <PeopleMultiSelect
      people={devs}
      selectedIds={draft.assigneeIds}
      onToggle={toggleAssignee}
      loading={loading}
    />
  </FormField>
</div>
</Modal>

<Modal
  open={timePrompt !== null}
  onOpenChange={(open) => !open && !saving && closeTimePrompt()}
  hideHeader
  showCancel={false}
  size="lg"
  footer={
    <div className="flex w-full justify-end gap-2">
      <Button type="button" variant="outline" onClick={closeTimePrompt} disabled={saving}>
        Annuler
      </Button>
      <Button
        type="button"
        onClick={confirmTime}
        disabled={saving}
        className="bg-gradient-to-r from-cyan-400 to-cyan-500 text-white shadow-md hover:from-cyan-500 hover:to-cyan-600"
      >
        {saving ? 'Enregistrement…' : 'Valider'}
      </Button>
    </div>
  }
>
  <div className="border-b border-gray-200 px-6 py-4 -mx-6 -mt-6 mb-4">
    <h2 className="text-2xl font-bold text-gray-800">
      {timePrompt?.timeOnly
        ? 'Modifier le temps'
        : timePrompt?.targetStatus === 'review'
          ? 'En révision — temps passé'
          : 'Ticket terminé — temps passé'}
    </h2>
    <p className="mt-2 text-sm text-gray-600">
      Nom de la tâche : <span className="font-medium text-gray-900">{timePrompt?.title}</span>
    </p>
  </div>
  <div className="space-y-4">
    <p className="text-sm text-gray-600">
      {timePromptLabels.canManageCp
        ? 'Indiquez le temps du développeur, celui du chef de projet, ou les deux.'
        : 'Indiquez votre temps passé sur ce ticket.'}
    </p>
    {timePromptLabels.canManageCp ? (
      <TimeEntryBlock
        title={timePromptLabels.cpTitle}
        optional
        draft={cpTime}
        onChange={(next) => {
          setCpTime(next)
          setTimeError('')
        }}
      />
    ) : null}
    <TimeEntryBlock
      title={timePromptLabels.devTitle}
      optional={timePromptLabels.canManageCp}
      draft={devTime}
      error={timeError || undefined}
      onChange={(next) => {
        setDevTime(next)
        setTimeError('')
      }}
    />
  </div>
</Modal>
</div>
)
}

type FilterKey = 'project' | 'cp' | 'dev'

const FILTER_FIELDS: Array<{
key: FilterKey
label: string
icon: typeof FolderKanban
}> = [
{ key: 'project', label: 'Projet', icon: FolderKanban },
{ key: 'cp', label: 'Chef de projet', icon: UserRound },
{ key: 'dev', label: 'Dev', icon: Code2 },
]

function useDismiss(open: boolean, onClose: () => void) {
const rootRef = useRef<HTMLDivElement>(null)

useEffect(() => {
if (!open) return
const onPointerDown = (event: MouseEvent) => {
if (!rootRef.current?.contains(event.target as Node)) onClose()
}
const onKeyDown = (event: KeyboardEvent) => {
if (event.key === 'Escape') onClose()
}
document.addEventListener('mousedown', onPointerDown)
document.addEventListener('keydown', onKeyDown)
return () => {
document.removeEventListener('mousedown', onPointerDown)
document.removeEventListener('keydown', onKeyDown)
}
}, [open, onClose])

return rootRef
}

function TicketFilterBar({
projects,
chefs,
devs,
query,
onQueryChange,
projectFilter,
cpFilter,
devFilter,
onProjectChange,
onCpChange,
onDevChange,
}: {
projects: TicketProject[]
chefs: Person[]
devs: Person[]
query: string
onQueryChange: (value: string) => void
projectFilter: string
cpFilter: string
devFilter: string
onProjectChange: (value: string) => void
onCpChange: (value: string) => void
onDevChange: (value: string) => void
}) {
const values: Record<FilterKey, string> = {
project: projectFilter,
cp: cpFilter,
dev: devFilter,
}
const setters: Record<FilterKey, (value: string) => void> = {
project: onProjectChange,
cp: onCpChange,
dev: onDevChange,
}
const activeKeys = FILTER_FIELDS.filter((field) => values[field.key] !== 'all')
const hasFilters = activeKeys.length > 0 || query.trim().length > 0

return (
<div className="relative z-20 flex flex-wrap items-center justify-end gap-2">
<label className="relative shrink-0">
<Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
<input
  value={query}
  onChange={(event) => onQueryChange(event.target.value)}
  placeholder="Rechercher"
  className="h-9 w-40 shrink-0 rounded-md border border-gray-200 bg-white pl-9 pr-3 text-sm text-gray-800 shadow-sm outline-none placeholder:text-gray-400 focus:border-[#14c4b0] focus:ring-2 focus:ring-[#14c4b0]/20"
/>
</label>

{activeKeys.map((field) => (
<FilterChip
  key={field.key}
  field={field}
  value={values[field.key]}
  projects={projects}
  chefs={chefs}
  devs={devs}
  onChange={setters[field.key]}
/>
))}

<div className="flex items-center gap-1">
<FilterAddMenu
  values={values}
  projects={projects}
  chefs={chefs}
  devs={devs}
  onChange={(key, value) => setters[key](value)}
/>

{hasFilters ? (
  <button
    type="button"
    onClick={() => {
      onQueryChange('')
      onProjectChange('all')
      onCpChange('all')
      onDevChange('all')
    }}
    className="inline-flex h-9 shrink-0 items-center px-1 text-sm text-gray-500 hover:text-gray-900"
  >
    Effacer
  </button>
) : null}
</div>
</div>
)
}

function FilterAddMenu({
values,
projects,
chefs,
devs,
onChange,
}: {
values: Record<FilterKey, string>
projects: TicketProject[]
chefs: Person[]
devs: Person[]
onChange: (key: FilterKey, value: string) => void
}) {
const [open, setOpen] = useState(false)
const [field, setField] = useState<FilterKey | null>(null)
const close = () => {
setOpen(false)
setField(null)
}
const rootRef = useDismiss(open, close)
const activeCount = FILTER_FIELDS.filter((item) => values[item.key] !== 'all').length

return (
<div ref={rootRef} className="relative shrink-0">
<button
type="button"
aria-expanded={open}
onClick={() => {
  setField(null)
  setOpen((current) => !current)
}}
className="inline-flex h-9 items-center gap-1.5 rounded-md border border-dashed border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 shadow-sm hover:border-gray-400 hover:bg-gray-50"
>
<ListFilter className="h-4 w-4 text-gray-500" />
Filtrer
{activeCount > 0 ? (
  <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[#14c4b0] px-1.5 text-[11px] font-semibold text-white">
    {activeCount}
  </span>
) : null}
</button>
{open ? (
<div className="absolute left-0 z-30 mt-2 w-64 rounded-2xl border border-gray-200 bg-white p-1.5 shadow-xl">
  {field ? (
    <FilterValueList
      field={field}
      value={values[field]}
      projects={projects}
      chefs={chefs}
      devs={devs}
      onBack={() => setField(null)}
      onSelect={(next) => {
        onChange(field, next)
        close()
      }}
    />
  ) : (
    <div className="py-1">
      <p className="px-2.5 pb-1 pt-1 text-[11px] font-medium uppercase tracking-wide text-gray-400">
        Filtrer par
      </p>
      {FILTER_FIELDS.map((item) => {
        const Icon = item.icon
        const selected = values[item.key] !== 'all'
        return (
          <button
            key={item.key}
            type="button"
            onClick={() => setField(item.key)}
            className="flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left text-sm text-gray-800 hover:bg-gray-50"
          >
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-gray-100 text-gray-600">
              <Icon className="h-3.5 w-3.5" />
            </span>
            {item.label}
            {selected ? <Check className="ml-auto h-4 w-4 text-[#14c4b0]" /> : null}
          </button>
        )
      })}
    </div>
  )}
</div>
) : null}
</div>
)
}

function FilterChip({
field,
value,
projects,
chefs,
devs,
onChange,
}: {
field: (typeof FILTER_FIELDS)[number]
value: string
projects: TicketProject[]
chefs: Person[]
devs: Person[]
onChange: (value: string) => void
}) {
const [open, setOpen] = useState(false)
const close = () => setOpen(false)
const rootRef = useDismiss(open, close)
const Icon = field.icon
const label = filterValueLabel(field.key, value, projects, chefs, devs)

return (
<div ref={rootRef} className="relative shrink-0">
<div className="inline-flex h-9 items-center rounded-md border border-gray-200 bg-white text-sm shadow-sm">
<button
  type="button"
  onClick={() => setOpen((current) => !current)}
  className="inline-flex h-9 max-w-[280px] items-center gap-1.5 rounded-md pl-1.5 pr-2"
>
  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#e7f8f5] text-[#0f9d8d]">
    <Icon className="h-3.5 w-3.5" />
  </span>
  <span className="text-gray-500">{field.label}</span>
  <span className="text-gray-300">est</span>
  <span className="truncate font-medium text-gray-900">{label}</span>
</button>
<button
  type="button"
  aria-label={`Retirer le filtre ${field.label}`}
  onClick={() => {
    onChange('all')
    close()
  }}
  className="mr-1 inline-flex h-6 w-6 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-700"
>
  <X className="h-3.5 w-3.5" />
</button>
</div>
{open ? (
<div className="absolute left-0 z-30 mt-2 w-64 rounded-2xl border border-gray-200 bg-white p-1.5 shadow-xl">
  <FilterValueList
    field={field.key}
    value={value}
    projects={projects}
    chefs={chefs}
    devs={devs}
    onSelect={(next) => {
      onChange(next)
      close()
    }}
  />
</div>
) : null}
</div>
)
}

function FilterValueList({
field,
value,
projects,
chefs,
devs,
onSelect,
onBack,
}: {
field: FilterKey
value: string
projects: TicketProject[]
chefs: Person[]
devs: Person[]
onSelect: (value: string) => void
onBack?: () => void
}) {
const options = filterOptions(field, projects, chefs, devs).filter(
  (option) => value !== 'all' || option.value !== 'all'
)
const meta = FILTER_FIELDS.find((item) => item.key === field)

return (
<div>
{onBack && meta ? (
<button
  type="button"
  onClick={onBack}
  className="mb-1 flex w-full items-center gap-2 rounded-xl px-2.5 py-2 text-left text-sm font-medium text-gray-800 hover:bg-gray-50"
>
  <ChevronLeft className="h-4 w-4 text-gray-400" />
  {meta.label}
</button>
) : null}
<div role="listbox" className="max-h-64 overflow-y-auto">
{options.map((option) => {
  const selected = option.value === value
  return (
    <button
      key={option.value}
      type="button"
      role="option"
      aria-selected={selected}
      onClick={() => onSelect(option.value)}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left text-sm text-gray-800 hover:bg-gray-50',
        selected && 'bg-[#f3fbf9]'
      )}
    >
      {option.swatch}
      <span className="truncate">{option.label}</span>
      {selected ? <Check className="ml-auto h-4 w-4 shrink-0 text-[#14c4b0]" /> : null}
    </button>
  )
})}
</div>
</div>
)
}

function filterValueLabel(
  field: FilterKey,
  value: string,
  projects: TicketProject[],
  chefs: Person[],
  devs: Person[]
): string {
  return filterOptions(field, projects, chefs, devs).find((option) => option.value === value)?.label ?? value
}

function filterOptions(
  field: FilterKey,
  projects: TicketProject[],
  chefs: Person[],
  devs: Person[]
): Array<{ value: string; label: string; swatch: ReactNode }> {
  if (field === 'project') {
    return [
      { value: 'all', label: 'Tous les projets', swatch: <span className="h-2.5 w-2.5 rounded-full bg-gray-300" /> },
      ...projects.map((project) => ({
        value: project.id,
        label: project.name,
        swatch: <span className={cn('h-2.5 w-2.5 rounded-full', project.dotClass)} />,
      })),
    ]
  }

  const people = (field === 'cp' ? chefs : devs).map((person) => ({
    value: person.id,
    label: person.name,
    swatch: (
      <span
        className="flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold text-white"
        style={{ backgroundColor: person.color }}
      >
        {person.initial}
      </span>
    ),
  }))

  return [
    {
      value: 'all',
      label: field === 'cp' ? 'Tous les CPs' : 'Tous les devs',
      swatch: <span className="h-2.5 w-2.5 rounded-full bg-gray-300" />,
    },
    ...people,
  ]
}

function KanbanColumn({
column,
tickets,
projects,
people,
currentUserId,
currentUserRole,
boardDragging,
onOpen,
}: {
column: (typeof COLUMNS)[number]
tickets: Ticket[]
projects: TicketProject[]
people: Person[]
currentUserId?: string
currentUserRole?: string
boardDragging: boolean
onOpen: (ticket: Ticket) => void
}) {
const { setNodeRef, isOver } = useDroppable({ id: column.id })

return (
<div className="min-w-[280px] flex-1">
<div
ref={setNodeRef}
className={cn(
  'overflow-hidden rounded-2xl border p-3 transition-shadow',
  column.surface,
  isOver && 'ring-2 ring-[#14c4b0]/50'
)}
>
<div className="mb-3 flex items-center justify-between px-1">
  <div className="flex items-center gap-2">
    <span className={cn('h-2.5 w-2.5 rounded-full ring-4 ring-white', column.dot)} />
    <span className="text-sm font-semibold text-gray-800">{column.label}</span>
  </div>
  <span
    className={cn(
      'inline-flex h-6 min-w-6 items-center justify-center rounded-full px-2 text-xs font-medium',
      column.count
    )}
  >
    {tickets.length}
  </span>
</div>
<SortableContext items={tickets.map((ticket) => ticket.id)} strategy={verticalListSortingStrategy}>
  <div className="space-y-2.5">
    {tickets.length === 0 ? (
      <p className="rounded-xl border border-dashed border-gray-200 px-3 py-8 text-center text-sm text-gray-400">
        Aucun ticket
      </p>
    ) : (
      tickets.map((ticket) => (
        <SortableTicketCard
          key={ticket.id}
          ticket={ticket}
          projects={projects}
          people={people}
          currentUserId={currentUserId}
          currentUserRole={currentUserRole}
          boardDragging={boardDragging}
          onOpen={onOpen}
        />
      ))
    )}
  </div>
</SortableContext>
</div>
</div>
)
}

function SortableTicketCard({
ticket,
projects,
people,
currentUserId,
currentUserRole,
boardDragging,
onOpen,
}: {
ticket: Ticket
projects: TicketProject[]
people: Person[]
currentUserId?: string
currentUserRole?: string
boardDragging: boolean
onOpen: (ticket: Ticket) => void
}) {
const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
id: ticket.id,
})

return (
<article
ref={setNodeRef}
style={{
transform: CSS.Transform.toString(transform),
// Transition seulement pendant un vrai drag : sinon l'ouverture de la
// modale (scrollbar) fait glisser/déborder les cartes dans les colonnes.
transition: boardDragging ? transition : undefined,
}}
className={cn(
'cursor-grab rounded-xl border border-black/[0.08] bg-white px-3.5 py-3 text-left transition-shadow hover:shadow-[0_8px_24px_-14px_rgba(15,23,42,0.35)] active:cursor-grabbing',
isDragging && 'opacity-40'
)}
{...attributes}
{...listeners}
onClick={() => onOpen(ticket)}
>
<TicketCardBody
  ticket={ticket}
  projects={projects}
  people={people}
  currentUserId={currentUserId}
  currentUserRole={currentUserRole}
/>
</article>
)
}

function TicketCard({
  ticket,
  projects,
  people,
  currentUserId,
  currentUserRole,
  overlay = false,
}: {
  ticket: Ticket
  projects: TicketProject[]
  people: Person[]
  currentUserId?: string
  currentUserRole?: string
  overlay?: boolean
}) {
return (
<article
className={cn(
'rounded-xl border border-black/[0.08] bg-white px-3.5 py-3',
overlay && 'rotate-1 shadow-lg'
)}
>
<TicketCardBody
  ticket={ticket}
  projects={projects}
  people={people}
  currentUserId={currentUserId}
  currentUserRole={currentUserRole}
/>
</article>
)
}

function TicketCardBody({
  ticket,
  projects,
  people,
  currentUserId,
  currentUserRole,
}: {
  ticket: Ticket
  projects: TicketProject[]
  people: Person[]
  currentUserId?: string
  currentUserRole?: string
}) {
  const project = findProject(projects, ticket.projectId)
  const chefs = ticket.cpIds
    .map((id) => findPerson(people, id))
    .filter((person) => person !== undefined)
  const chef = chefs[0]
  const assignees = ticket.assigneeIds
    .map((id) => findPerson(people, id))
    .filter((person) => person !== undefined)
  const delay = ticketDelay({
    status: ticket.status,
    dueDate: ticket.dueDate,
    doneAt: ticket.doneAt,
    reviewedAt: ticket.reviewedAt,
  })
  const closedLate = (ticket.status === 'review' || ticket.status === 'done') && delay.late
  const openOverdue = (ticket.status === 'todo' || ticket.status === 'doing') && delay.late
  
  return (
  <>
  {project ? (
  <span
    className={cn(
      'inline-block max-w-full truncate rounded-full px-2 py-0.5 text-[11px] font-medium',
      project.badgeClass
    )}
  >
    {project.name}
  </span>
  ) : null}
  <h2 className="mt-2 text-sm font-medium leading-snug text-gray-950">{ticket.title}</h2>
  {ticket.description ? (
  <TextWithLinks
    text={ticket.description}
    className="mt-1 line-clamp-2 text-[13px] leading-relaxed text-gray-500"
  />
  ) : null}
  <div className="mt-3 flex items-center justify-between gap-2">
  {chef ? (
    <span className="inline-flex min-w-0 items-center gap-1.5 text-xs text-gray-600">
      <span
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white"
        style={{ backgroundColor: chef.color }}
      >
        {chef.initial}
      </span>
      <span className="truncate font-medium text-gray-800" title={chefs.map((person) => person.name).join(', ')}>
        {chef.name}
      </span>
      {chefs.length > 1 ? (
        <span className="shrink-0 text-gray-400">+{chefs.length - 1}</span>
      ) : null}
    </span>
  ) : (
    <span />
  )}
  <div className="flex shrink-0 items-center gap-2">
    {assignees.length > 0 ? (
      <div className="flex -space-x-1.5">
        {assignees.map((person) => (
          <span
            key={person.id}
            title={person.name}
            className="flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold text-white ring-2 ring-white"
            style={{ backgroundColor: person.color }}
          >
            {person.initial}
          </span>
        ))}
      </div>
    ) : null}
    {(ticket.status === 'review' || ticket.status === 'done') &&
    (ticket.timeSpentDays ||
      (canManageCpTime(currentUserId, currentUserRole, ticket.cpIds) && ticket.cpTimeSpentDays)) ? (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-[#0f9d8d]">
        <Clock className="h-3.5 w-3.5" />
        {ticket.timeSpentDays ? formatTimeSpent(ticket.timeSpentDays, ticket.timeSpentUnit) : null}
        {canManageCpTime(currentUserId, currentUserRole, ticket.cpIds) && ticket.cpTimeSpentDays
          ? `${ticket.timeSpentDays ? ' · ' : ''}CP ${formatTimeSpent(ticket.cpTimeSpentDays, ticket.cpTimeSpentUnit)}`
          : null}
      </span>
    ) : null}
    {closedLate ? (
      <span
        className="inline-flex items-center gap-1 text-xs font-medium text-rose-500"
        title={ticket.reviewedAt ? 'Retard figé au passage en révision' : 'Retard à la clôture'}
      >
        <Calendar className="h-3.5 w-3.5" />
        {formatDueDate(ticket.dueDate)} · {delay.text}
      </span>
    ) : ticket.status === 'done' && ticket.doneAt ? (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
        <Clock className="h-3.5 w-3.5" />
        {formatDaysPassed(ticket.doneAt)}
      </span>
    ) : ticket.status !== 'done' ? (
      <span
        className={cn(
          'inline-flex items-center gap-1 text-xs',
          openOverdue ? 'font-medium text-rose-500' : 'text-gray-400'
        )}
      >
        <Calendar className="h-3.5 w-3.5" />
        {formatDueDate(ticket.dueDate)}
      </span>
    ) : null}
  </div>
  </div>
  </>
  )
  }
  
